import React, { useEffect, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  Icon,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  PaginationComponent,
} from "../../../components/Component";
import { Modal, ModalBody, Form, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import PermissionGate from "../../../components/permissions/PermissionGate";

const paginatedRows = (res) => (Array.isArray(res?.data) ? res.data : []);

const IMPACTS = [
  { value: "equipment", label: "Équipement" },
  { value: "process", label: "Procédé" },
  { value: "chemical", label: "Produit chimique" },
  { value: "organizational", label: "Organisationnel" },
];

const HseProcessMocPage = () => {
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [itemPerPage] = useState(10);
  const [listSearch, setListSearch] = useState("");
  const [listPage, setListPage] = useState(1);

  const { register, handleSubmit, reset } = useForm();
  const raForm = useForm();
  const reviewForm = useForm();
  const postForm = useForm();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getMocRequests({ per_page: 100 });
      setRows(paginatedRows(res));
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    (async () => {
      try {
        const [sRes, uRes] = await Promise.all([api.getSites(), api.getUsers({ per_page: 300 })]);
        setSites(sRes.data || []);
        setUsers(paginatedRows(uRes));
      } catch (e) {
        /* ignore */
      }
    })();
  }, []);

  useEffect(() => {
    setListPage(1);
  }, [listSearch]);

  const norm = (v) => String(v ?? "").toLowerCase();
  const filteredRows = rows.filter((r) => {
    if (!listSearch.trim()) return true;
    const q = norm(listSearch);
    return (
      norm(r.title).includes(q) ||
      norm(r.status).includes(q) ||
      norm(r.site?.name).includes(q) ||
      String(r.id).includes(listSearch.trim())
    );
  });
  const listLast = listPage * itemPerPage;
  const listFirst = listLast - itemPerPage;
  const pageRows = filteredRows.slice(listFirst, listLast);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const m = await api.getMocRequest(id);
      setDetail(m);
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const mid = q.get("open_moc");
    if (!mid || !/^\d+$/.test(mid)) return;
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      setDetail(null);
      try {
        const m = await api.getMocRequest(mid);
        if (!cancelled) setDetail(m);
      } catch (e) {
        if (!cancelled) toast.error(e.message || "MOC introuvable");
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
      if (!cancelled) {
        q.delete("open_moc");
        const s = q.toString();
        window.history.replaceState({}, "", `${location.pathname}${s ? `?${s}` : ""}${location.hash || ""}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search, location.pathname, location.hash]);

  const refreshDetail = async () => {
    if (detail?.id) await openDetail(detail.id);
    load();
  };

  const onCreate = async (data) => {
    const impact_categories = IMPACTS.filter((i) => data[`impact_${i.value}`]).map((i) => i.value);
    try {
      await api.createMocRequest({
        site_id: data.site_id ? parseInt(data.site_id, 10) : null,
        title: data.title,
        description: data.description || null,
        impact_categories,
      });
      toast.success("MOC créé");
      setCreateOpen(false);
      load();
    } catch (e) {
      const msg = e.errors ? Object.values(e.errors).flat().join(", ") : e.message;
      toast.error(msg || "Erreur");
    }
  };

  const userLabel = (u) => (u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email : "—");

  return (
    <React.Fragment>
      <Head title="Management of Change" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Management of Change (MOC)</BlockTitle>
              <BlockDes className="text-soft">
                Tout changement (équipement, process, chimique…) : analyse d’impact, double validation HSE / direction, actions
                de mise en œuvre puis audit post-changement.
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <BlockBetween className="mb-3">
            <BlockHeadContent>
              <div className="form-control-wrap" style={{ minWidth: 260 }}>
                <div className="form-icon form-icon-left">
                  <Icon name="search" />
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher un MOC…"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                />
              </div>
              {!loading && (
                <BlockDes className="text-soft mt-1 mb-0">
                  <p className="small mb-0">
                    {listSearch
                      ? `${filteredRows.length} MOC sur ${rows.length}`
                      : `${rows.length} MOC`}
                  </p>
                </BlockDes>
              )}
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
                <PermissionGate permission={["MOC - Créer"]}>
                  <li>
                    <Button
                      color="primary"
                      size="md"
                      onClick={() => {
                        const empty = { title: "", description: "", site_id: "" };
                        IMPACTS.forEach((i) => {
                          empty[`impact_${i.value}`] = false;
                        });
                        reset(empty);
                        setCreateOpen(true);
                      }}
                    >
                      <Icon name="plus" />
                      <span>Nouveau MOC</span>
                    </Button>
                  </li>
                </PermissionGate>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
          {loading ? (
            <div className="card card-stretch">
              <div className="card-inner text-center py-5">
                <Spinner />
              </div>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="card card-stretch">
              <div className="card-inner text-center py-5">
                <p className="text-soft mb-0">
                  {listSearch ? "Aucun MOC ne correspond à la recherche" : "Aucun MOC"}
                </p>
              </div>
            </div>
          ) : (
            <>
              <DataTable className="card-stretch">
                <DataTableBody>
                  <DataTableHead className="nk-tb-item nk-tb-head">
                    <DataTableRow size="xl">
                      <span className="sub-text">Titre</span>
                    </DataTableRow>
                    <DataTableRow size="lg">
                      <span className="sub-text">Site</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span className="sub-text">Statut</span>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools text-end">
                      <span className="sub-text">Actions</span>
                    </DataTableRow>
                  </DataTableHead>
                  {pageRows.map((r) => (
                    <DataTableItem key={r.id}>
                      <DataTableRow size="xl">
                        <span className="tb-lead">{r.title}</span>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span className="tb-sub">{r.site?.name || "—"}</span>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <span className="badge badge-dim bg-outline-primary">{r.status}</span>
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1">
                          <li>
                            <button
                              type="button"
                              className="btn btn-trigger btn-icon"
                              title="Ouvrir"
                              onClick={() => openDetail(r.id)}
                            >
                              <Icon name="eye" />
                            </button>
                          </li>
                        </ul>
                      </DataTableRow>
                    </DataTableItem>
                  ))}
                </DataTableBody>
              </DataTable>
              <div className="card-inner">
                <PaginationComponent
                  itemPerPage={itemPerPage}
                  totalItems={filteredRows.length}
                  paginate={setListPage}
                  currentPage={listPage}
                />
              </div>
            </>
          )}
        </Block>

        <Modal isOpen={createOpen} toggle={() => setCreateOpen(false)} className="modal-lg">
          <div className="modal-header">
            <h5 className="modal-title">Nouveau MOC</h5>
            <button type="button" className="close" onClick={() => setCreateOpen(false)}>
              <Icon name="cross" />
            </button>
          </div>
          <ModalBody>
            <Form className="is-alter" onSubmit={handleSubmit(onCreate)}>
              <div className="form-group">
                <label className="form-label">Titre *</label>
                <input className="form-control" name="title" ref={register({ required: true })} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" name="description" rows={3} ref={register} />
              </div>
              <div className="form-group">
                <label className="form-label">Site</label>
                <select className="form-select" name="site_id" ref={register}>
                  <option value="">—</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Impacts</label>
                {IMPACTS.map((i) => (
                  <div key={i.value} className="custom-control custom-checkbox">
                    <input type="checkbox" className="custom-control-input" id={`imp_${i.value}`} name={`impact_${i.value}`} ref={register} />
                    <label className="custom-control-label" htmlFor={`imp_${i.value}`}>
                      {i.label}
                    </label>
                  </div>
                ))}
              </div>
              <div className="text-end">
                <Button color="primary" type="submit">
                  Créer
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>

        <Modal
          isOpen={detail !== null || detailLoading}
          toggle={() => {
            setDetail(null);
            setDetailLoading(false);
          }}
          className="modal-lg"
        >
          <div className="modal-header">
            <h5 className="modal-title">{detail?.title || "…"}</h5>
            <button type="button" className="close" onClick={() => setDetail(null)}>
              <Icon name="cross" />
            </button>
          </div>
          <ModalBody>
            {detailLoading && <Spinner />}
            {detail && !detailLoading && (
              <>
                <p className="text-soft small">Statut : {detail.status}</p>
                <p>{detail.description}</p>

                <h6 className="mt-3">Analyse de risques</h6>
                <Form
                  className="is-alter"
                  onSubmit={raForm.handleSubmit(async (d) => {
                    try {
                      await api.postMocRiskAssessment(detail.id, {
                        hazards: d.hazards || null,
                        controls: d.controls || null,
                        residual_risk: d.residual_risk || null,
                        is_complete: d.is_complete === true || d.is_complete === "true",
                      });
                      toast.success("Analyse enregistrée");
                      await refreshDetail();
                    } catch (e) {
                      toast.error(e.message || "Erreur");
                    }
                  })}
                >
                  <textarea className="form-control mb-2" name="hazards" placeholder="Risques" ref={raForm.register} rows={2} />
                  <textarea className="form-control mb-2" name="controls" placeholder="Mesures" ref={raForm.register} rows={2} />
                  <input className="form-control mb-2" name="residual_risk" placeholder="Risque résiduel" ref={raForm.register} />
                  <div className="custom-control custom-checkbox mb-2">
                    <input type="checkbox" className="custom-control-input" id="ra_complete" name="is_complete" ref={raForm.register} />
                    <label className="custom-control-label" htmlFor="ra_complete">
                      Analyse complète
                    </label>
                  </div>
                  <PermissionGate permission={["MOC - Modifier"]}>
                    <Button color="primary" size="sm" type="submit">
                      Enregistrer l’analyse
                    </Button>
                  </PermissionGate>
                </Form>

                {detail.status === "draft" && (
                  <Form
                    className="is-alter mt-3"
                    onSubmit={reviewForm.handleSubmit(async (d) => {
                      try {
                        await api.submitMocForReview(detail.id, {
                          hse_approver_id: parseInt(d.hse_approver_id, 10),
                          management_approver_id: parseInt(d.management_approver_id, 10),
                        });
                        toast.success("Soumis en revue");
                        await refreshDetail();
                      } catch (e) {
                        toast.error(e.message || "Erreur");
                      }
                    })}
                  >
                    <h6>Soumettre pour revue</h6>
                    <select className="form-select mb-2" name="hse_approver_id" ref={reviewForm.register({ required: true })}>
                      <option value="">Approbateur HSE</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {userLabel(u)}
                        </option>
                      ))}
                    </select>
                    <select className="form-select mb-2" name="management_approver_id" ref={reviewForm.register({ required: true })}>
                      <option value="">Approbateur management</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {userLabel(u)}
                        </option>
                      ))}
                    </select>
                    <PermissionGate permission={["MOC - Modifier"]}>
                      <Button color="primary" size="sm" type="submit">
                        Soumettre
                      </Button>
                    </PermissionGate>
                  </Form>
                )}

                <h6 className="mt-3">Approbations</h6>
                <ul className="list list-sm">
                  {(detail.approvals || []).map((a) => (
                    <li key={a.id}>
                      {a.role_label} — {a.status} — {userLabel(a.approver)}
                      {a.status === "pending" && (
                        <PermissionGate permission={["MOC - Approuver"]}>
                          <Button
                            color="success"
                            size="sm"
                            className="ms-2"
                            onClick={async () => {
                              try {
                                await api.approveMoc(detail.id, a.id, {});
                                toast.success("Approuvé");
                                await refreshDetail();
                              } catch (e) {
                                toast.error(e.message || "Erreur");
                              }
                            }}
                          >
                            Approuver
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            className="ms-1"
                            onClick={async () => {
                              const c = window.prompt("Commentaire refus");
                              if (!c) return;
                              try {
                                await api.rejectMoc(detail.id, a.id, { comments: c });
                                await refreshDetail();
                              } catch (e) {
                                toast.error(e.message || "Erreur");
                              }
                            }}
                          >
                            Refuser
                          </Button>
                        </PermissionGate>
                      )}
                    </li>
                  ))}
                </ul>

                {detail.status === "approved" && (
                  <PermissionGate permission={["MOC - Modifier"]}>
                    <Button color="primary" size="sm" className="me-2" onClick={async () => {
                      try {
                        await api.startMocImplementation(detail.id);
                        toast.success("Mise en œuvre démarrée");
                        await refreshDetail();
                      } catch (e) {
                        toast.error(e.message || "Erreur");
                      }
                    }}>
                      Démarrer mise en œuvre
                    </Button>
                  </PermissionGate>
                )}
                {detail.status === "in_implementation" && (
                  <PermissionGate permission={["MOC - Modifier"]}>
                    <Button color="primary" size="sm" onClick={async () => {
                      try {
                        await api.completeMocImplementation(detail.id);
                        toast.success("Mise en œuvre terminée");
                        await refreshDetail();
                      } catch (e) {
                        toast.error(e.message || "Erreur");
                      }
                    }}>
                      Terminer mise en œuvre
                    </Button>
                  </PermissionGate>
                )}
                {detail.status === "post_review_pending" && (
                  <Form
                    className="is-alter mt-2"
                    onSubmit={postForm.handleSubmit(async (d) => {
                      try {
                        await api.submitMocPostReview(detail.id, { post_review_notes: d.post_review_notes });
                        toast.success("MOC clôturé");
                        await refreshDetail();
                      } catch (e) {
                        toast.error(e.message || "Erreur");
                      }
                    })}
                  >
                    <h6>Revue post-implémentation</h6>
                    <textarea className="form-control mb-2" name="post_review_notes" ref={postForm.register({ required: true })} rows={3} />
                    <PermissionGate permission={["MOC - Modifier"]}>
                      <Button color="primary" size="sm" type="submit">
                        Clôturer
                      </Button>
                    </PermissionGate>
                  </Form>
                )}
              </>
            )}
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default HseProcessMocPage;
